import type {
  StorageOptions,
  StorageResult,
  DownloadOptions,
  DownloadResult,
  IStorageProvider,
  StorageError
} from './types.js'
import { GitHubStorageProvider } from './providers/github.provider.js'
import { FirebaseStorageProvider } from './providers/firebase.provider.js'
import { S3StorageProvider } from './providers/s3.provider.js'
import { LocalStorageProvider } from './providers/local.provider.js'
import { resolveFiles, getTimestamp, formatDuration } from './utils.js'

/**
 * Main storage manager class
 */
export class StorageManager {
  private provider: IStorageProvider
  private options: StorageOptions

  constructor(options: StorageOptions) {
    this.options = options
    this.provider = this.createProvider()
  }

  /**
   * Create the appropriate storage provider based on configuration
   */
  private createProvider(): IStorageProvider {
    const { storage, verbose } = this.options

    switch (storage.provider) {
      case 'github':
        return new GitHubStorageProvider(storage.config, verbose)

      case 'firebase':
        return new FirebaseStorageProvider(storage.config, verbose)

      case 's3':
        return new S3StorageProvider(storage.config, verbose)

      case 'local':
        return new LocalStorageProvider(storage.config, verbose)

      default:
        throw new Error(`Unsupported storage provider: ${(storage as any).provider}`)
    }
  }

  /**
   * Upload files to storage
   */
  async upload(): Promise<StorageResult> {
    const startTime = Date.now()
    const startedAt = getTimestamp()
    const errors: StorageError[] = []

    try {
      // Initialize provider
      await this.provider.initialize()

      // Resolve files from patterns/paths
      const filesToUpload = this.options.files
        ? await resolveFiles(this.options.files)
        : []

      if (filesToUpload.length === 0) {
        throw new Error('No files specified for upload')
      }

      if (this.options.verbose) {
        console.log(`📦 Uploading ${filesToUpload.length} file(s) to ${this.provider.name}...`)
      }

      // Upload files
      const uploadedFiles = await this.provider.uploadFiles(
        filesToUpload,
        this.options.onProgress
      )

      // Calculate total size
      const totalSize = uploadedFiles.reduce((sum, file) => sum + file.size, 0)

      const completedAt = getTimestamp()
      const duration = Date.now() - startTime

      if (this.options.verbose) {
        console.log(`✅ Upload completed in ${formatDuration(duration)}`)
      }

      // Build metadata based on provider
      const metadata: StorageResult['metadata'] = {
        provider: this.provider.name,
        totalFiles: uploadedFiles.length,
        totalSize,
        duration,
        startedAt,
        completedAt
      }

      // Add provider-specific metadata
      if (this.provider instanceof GitHubStorageProvider) {
        metadata.artifactInfo = {
          artifactName: this.provider.getArtifactName(),
          artifactId: this.provider.getArtifactId(),
          size: totalSize
        }
      } else if (this.provider instanceof FirebaseStorageProvider) {
        metadata.bucketInfo = {
          bucket: this.options.storage.provider === 'firebase'
            ? this.options.storage.config.bucket
            : ''
        }
      } else if (this.provider instanceof S3StorageProvider) {
        metadata.bucketInfo = {
          bucket: this.options.storage.provider === 's3'
            ? this.options.storage.config.bucket
            : '',
          region: this.options.storage.provider === 's3'
            ? this.options.storage.config.region
            : undefined
        }
      }

      return {
        success: true,
        files: uploadedFiles,
        metadata
      }
    } catch (error) {
      const err = error as Error
      errors.push({
        code: 'UPLOAD_FAILED',
        message: err.message,
        originalError: err
      })

      return {
        success: false,
        files: [],
        metadata: {
          provider: this.provider.name,
          totalFiles: 0,
          totalSize: 0,
          duration: Date.now() - startTime,
          startedAt,
          completedAt: getTimestamp()
        },
        errors
      }
    } finally {
      // Cleanup
      await this.provider.cleanup()
    }
  }

  /**
   * Check if a file exists
   */
  async fileExists(remotePath: string): Promise<boolean> {
    await this.provider.initialize()
    try {
      return await this.provider.fileExists(remotePath)
    } finally {
      await this.provider.cleanup()
    }
  }

  /**
   * Delete a file
   */
  async deleteFile(remotePath: string): Promise<void> {
    await this.provider.initialize()
    try {
      await this.provider.deleteFile(remotePath)
    } finally {
      await this.provider.cleanup()
    }
  }

  /**
   * List files in a path
   */
  async listFiles(remotePath?: string): Promise<string[]> {
    await this.provider.initialize()
    try {
      return await this.provider.listFiles(remotePath)
    } finally {
      await this.provider.cleanup()
    }
  }

  /**
   * Download files from storage
   */
  async download(files: string[]): Promise<DownloadResult> {
    const startTime = Date.now()
    const startedAt = getTimestamp()
    const errors: StorageError[] = []

    try {
      // Initialize provider
      await this.provider.initialize()

      if (files.length === 0) {
        throw new Error('No files specified for download')
      }

      if (this.options.verbose) {
        console.log(`📥 Downloading ${files.length} file(s) from ${this.provider.name}...`)
      }

      // Download files
      const downloadedFiles = await this.provider.downloadFiles(files)

      // Calculate total size
      const totalSize = downloadedFiles.reduce((sum, file) => sum + file.size, 0)

      const completedAt = getTimestamp()
      const duration = Date.now() - startTime

      if (this.options.verbose) {
        console.log(`✅ Download completed in ${formatDuration(duration)}`)
      }

      return {
        success: true,
        files: downloadedFiles,
        metadata: {
          provider: this.provider.name,
          totalFiles: downloadedFiles.length,
          totalSize,
          duration,
          startedAt,
          completedAt
        }
      }
    } catch (error) {
      const err = error as Error
      errors.push({
        code: 'DOWNLOAD_FAILED',
        message: err.message,
        originalError: err
      })

      return {
        success: false,
        files: [],
        metadata: {
          provider: this.provider.name,
          totalFiles: 0,
          totalSize: 0,
          duration: Date.now() - startTime,
          startedAt,
          completedAt: getTimestamp()
        },
        errors
      }
    } finally {
      // Cleanup
      await this.provider.cleanup()
    }
  }
}

/**
 * Convenience function to upload files
 */
export async function uploadFiles(options: StorageOptions): Promise<StorageResult> {
  const manager = new StorageManager(options)
  return manager.upload()
}

/**
 * Convenience function to download files
 */
export async function downloadFiles(options: DownloadOptions): Promise<DownloadResult> {
  // Convert DownloadOptions to StorageOptions
  const storageOptions: StorageOptions = {
    storage: options.storage,
    verbose: options.verbose,
    files: [] // Not used for download
  }
  const manager = new StorageManager(storageOptions)
  const files = typeof options.files[0] === 'string'
    ? options.files as string[]
    : (options.files as any[]).map((f: any) => f.remotePath)
  return manager.download(files)
}
